import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../../redux/store";
import BookLocationNavigateBackAction from "../../common/util/BookLocationNavigateBackAction";
import TenantLogo from "../../../layout/components/TenantLogo";
import Header from "../../../layout/components/Header";
import CloseIcon from "../../../../components/icons/CloseIcon";
import { Button } from "../../../../components/ui/button";
import BookLocationHeader from "../../common/util/BookLocationHeader";
import GeneralDialog from "../../../common/GeneralDialog";
import VoucherPaymentStep from "../steps/VoucherPaymentStep";
import BookLocationNavigateNextAction from "../../common/util/BookLocationNavigateNextAction";
import VoucherBookingSummaryStep from "../steps/VoucherBookingSummaryStep";
import VoucherOrderDataStep from "../steps/VoucherOrderDataStep";
import routes from "../../../../routes/routes";
import { cn } from "lib/utils";
import {
  bookingCreationCompleted,
  bookingFlowChanged,
  setNextEnabled,
  setUseCase,
} from "../../../../redux/slice/bookingCreation/bookingCreationSlice";

export enum BookingCreationStep {
  orderData,
  bookingSummary,
  payment,
}

function GiftVoucherPage() {
  const stepWrapperRef = useRef<HTMLElement>();
  const bottomSentinelRef = useRef<HTMLDivElement | null>(null);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    step,
    personalDataValidation,
    voucherSelectedAmount,
    voucherCustomAmountIsValid,
  } = useSelector((state: RootState) => state.bookingCreation);
  const [pageDialogOpen, setPageDialogOpen] = useState<boolean>(false);
  const [atBottom, setAtBottom] = useState<boolean>(false);
  const isLastStep = step === BookingCreationStep.payment;

  const closePage = () => {
    navigate(routes.home);
    dispatch(bookingCreationCompleted());
  };

  const headerText = useMemo(() => {
    switch (step) {
      case BookingCreationStep.orderData:
        return t("book_location.buy_gift_card");
      case BookingCreationStep.bookingSummary:
        return t("book_location.git_card_summary");
      default:
        return "";
    }
  }, [step]);

  useEffect(() => {
    stepWrapperRef.current = document.getElementById(
      "voucherBookingProcessStepWrapper"
    )!;
    if (step === BookingCreationStep.orderData) {
      dispatch(setUseCase({ useCase: "voucher" }));

      dispatch(
        bookingFlowChanged({
          bookingFlow: ["orderData", "bookingSummary", "payment"],
        })
      );
    }
  }, []);

  useEffect(() => {
    if (
      personalDataValidation.email &&
      personalDataValidation.firstName &&
      personalDataValidation.lastName &&
      personalDataValidation.isAgreed
    ) {
      if (
        voucherSelectedAmount === t("general.voucher_purchase.custom_amount")
      ) {
        if (voucherCustomAmountIsValid) {
          dispatch(setNextEnabled(true));
        } else {
          dispatch(setNextEnabled(false));
        }
      } else if (voucherSelectedAmount) {
        dispatch(setNextEnabled(true));
      } else {
        dispatch(setNextEnabled(false));
      }
    }
  }, [
    personalDataValidation,
    voucherCustomAmountIsValid,
    dispatch,
    voucherSelectedAmount,
    t,
  ]);

  useEffect(() => {
    const manageReduxData = async () => {
      if (step === BookingCreationStep.bookingSummary) {
        dispatch(setNextEnabled(true));
      }
    };
    manageReduxData();
  }, [step]);

  // Observe whether the bottom sentinel (placed just before the sticky footer)
  // is visible. If it is, the user has scrolled to the end of the content and
  // the footer's top border is no longer needed as a separator.
  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          setAtBottom(entry.isIntersecting);
        }
      },
      { threshold: 0, rootMargin: "0px 0px 1px 0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [step, isLastStep]);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <Header>
        <div className="h-12 absolute left-2 flex items-center">
          <BookLocationNavigateBackAction />
        </div>
        <TenantLogo />
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setPageDialogOpen(true)}
          aria-label={t("common.close", { defaultValue: "Close" })}
          className="absolute right-2 top-1/2 -translate-y-1/2 h-12 w-12 rounded-full text-foreground/90"
        >
          <CloseIcon />
        </Button>
      </Header>

      <section className="flex-1">
        <div className="max-w-[1000px] mx-auto px-4">
          {headerText && <BookLocationHeader text={headerText} />}
          <div className="mt-6">
            {step === BookingCreationStep.orderData && <VoucherOrderDataStep />}
            {step === BookingCreationStep.bookingSummary && <VoucherBookingSummaryStep />}
            {step === BookingCreationStep.payment && <VoucherPaymentStep />}
          </div>
          {!isLastStep && (
            <div ref={bottomSentinelRef} aria-hidden={true} className="h-px" />
          )}
        </div>
      </section>

      {!isLastStep && (
        <div
          className={cn(
            "sticky bottom-0 z-10 bg-background border-t max-w-[1000px] mx-auto w-full px-4 pt-3 transition-colors duration-150",
            atBottom ? "border-transparent" : "border-foreground/10"
          )}
          style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0))" }}
        >
          <BookLocationNavigateNextAction />
        </div>
      )}

      <GeneralDialog
        open={pageDialogOpen}
        onClose={() => setPageDialogOpen(false)}
        onContinue={closePage}
        content={t("use_order.closing_dialog.content")}
      />
    </div>
  );
}

export default GiftVoucherPage;
